
import { Injectable } from '@angular/core';
import { Prescription, PrescriptionDetail, Customer, CategoryQuota } from '../models/index';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';
import { MOCK_PRESCRIPTIONS } from '../data/mock-prescriptions';
import { MOCK_CUSTOMERS } from '../data/mock-customers'; 

const PRESCRIPTION_TABLE = 'prescriptions';

type VitalsData = Partial<Pick<Prescription, 'temperature_c' | 'pulse_rate' | 'respiration_rate' | 'blood_pressure' | 'weight_kg' | 'height_cm'>>;

@Injectable({ providedIn: 'root' })
export class PrescriptionService {
  private supabase: SupabaseClient | null = getSupabase();

  // --- NEW: Public Verification Method (Updated Mock) ---
  async verifyPrescriptionPublic(id: string): Promise<any> {
    if (!isSupabaseConfigured() || !this.supabase) {
        // Mock Response for verification
        const mockP = MOCK_PRESCRIPTIONS.find(p => p.id === id);
        if (mockP) {
            const clinic = {
                name: 'Mock Clinic HQ',
                address: '123 Test Road, Bangkok',
                phone: '02-999-9999',
                licenseNumber: '101010001234'
            };
            return {
                found: true,
                id: mockP.id,
                status: mockP.status,
                issueDate: mockP.createdAt,
                expiredAt: mockP.expiresAt,
                doctorName: mockP.doctorName || 'Dr. Mock Somchai',
                clinicName: clinic.name,
                clinicAddress: clinic.address,
                clinicPhone: clinic.phone,
                clinicLicense: clinic.licenseNumber,
                patientNameMasked: 'Mo******'
            };
        }
        return { found: false, message: 'Mock data not found' };
    }

    const { data, error } = await this.supabase.rpc('get_public_verification_data', { p_id: id });
    
    if (error) throw error;
    return data;
  }
  // ---------------------------------------

  async createPrescription(
    customerId: string, 
    storeId: string, 
    cashierId: string, 
    symptoms: string[] = [], 
    vitals?: VitalsData,
    referred_by_store_id?: string,
    referred_by_store_name?: string,
    referred_to_store_id?: string
  ): Promise<Prescription> {
      if (!isSupabaseConfigured() || !this.supabase) {
        // Mock Implementation
        const newPrescription: Prescription = {
            id: `mock-pres-${Date.now()}`,
            customerId,
            storeId,
            createdById: cashierId,
            status: 'PENDING',
            symptoms,
            createdAt: new Date().toISOString(),
            ...vitals,
            referred_by_store_id,
            referred_by_store_name,
            referred_to_store_id,
        };
        MOCK_PRESCRIPTIONS.push(newPrescription);
        return newPrescription;
      }
      
      const { data, error } = await this.supabase.rpc('create_prescription_secure', {
          p_customer_id: customerId,
          p_store_id: storeId,
          p_created_by_id: cashierId,
          p_symptoms: symptoms,
          p_temperature_c: vitals?.temperature_c,
          p_pulse_rate: vitals?.pulse_rate,
          p_respiration_rate: vitals?.respiration_rate,
          p_blood_pressure: vitals?.blood_pressure,
          p_weight_kg: vitals?.weight_kg,
          p_height_cm: vitals?.height_cm,
          p_referred_by_store_id: referred_by_store_id,
          p_referred_by_store_name: referred_by_store_name,
          p_referred_to_store_id: referred_to_store_id
      });

      if (error) throw new Error(error.message);
      
      return {
          id: data.id,
          customerId,
          storeId,
          createdById: cashierId,
          status: 'PENDING',
          symptoms,
          createdAt: new Date().toISOString(),
          ...vitals,
          referred_by_store_id,
          referred_by_store_name,
          referred_to_store_id
      } as Prescription;
  }
  
  async createApprovedPrescription(customer: Customer, details: PrescriptionDetail, doctorId: string): Promise<Prescription> {
    if (!isSupabaseConfigured() || !this.supabase) throw new Error("Database not connected");
    const { data, error } = await this.supabase.rpc('create_approved_prescription', {
        p_customer_id: customer.id,
        p_approved_items: details,
        p_doctor_id: doctorId
    }).select().single();
    
    if (error) {
      if (error.message.includes('INSUFFICIENT_FUNDS')) {
          throw new Error('ไม่สามารถอนุมัติได้: โควต้าแพ็กเกจหมด และเครดิตใน Wallet ไม่เพียงพอ กรุณาเติมเงินเข้าระบบ (Quota Exceeded & Insufficient Wallet Balance)');
      }
      throw new Error(error.message);
    }
    return data as Prescription;
  }

  async getPrescription(prescriptionId: string): Promise<Prescription | null> {
    if (!isSupabaseConfigured() || !this.supabase) return null;
    const { data, error } = await this.supabase
        .from(PRESCRIPTION_TABLE)
        .select(`*, customer:customers(full_name, citizen_id, birth_date, phone)`)
        .eq('id', prescriptionId)
        .single();

    if (error) { if (error.code === 'PGRST116') return null; throw new Error(error.message); }

    return {
        ...data,
        storeId: data.store_id,
        customerId: data.customer_id,
        createdById: data.created_by_id,
        approvedItems: data.approved_items,
        createdAt: data.createdAt, 
        reviewedAt: data.reviewed_at,
        evidenceUrl: data.evidence_url,
        customerName: data.customer?.full_name,
        customerCitizenId: data.customer?.citizen_id,
        customerBirthDate: data.customer?.birth_date,
        doctorName: data.doctor_name,
        doctorSignatureSnapshot: data.doctor_signature_snapshot
    } as Prescription;
  }

  async getCustomerHistory(customerId: string, page: number = 0, pageSize: number = 10): Promise<Prescription[]> {
    if (!isSupabaseConfigured() || !this.supabase) return MOCK_PRESCRIPTIONS.filter(p => p.customerId === customerId);
    try {
        const from = page * pageSize;
        const to = from + pageSize - 1;
        const { data, error } = await this.supabase
            .from(PRESCRIPTION_TABLE)
            .select('*')
            .eq('customer_id', customerId)
            .in('status', ['APPROVED', 'USED']) 
            .order('createdAt', { ascending: false }) 
            .range(from, to);

        if (error) throw new Error(error.message);
        return data.map((p: any) => ({
            ...p,
            customerId: p.customer_id,
            storeId: p.store_id,
            createdById: p.created_by_id,
            approvedItems: p.approved_items,
            createdAt: p.createdAt, 
            reviewedAt: p.reviewed_at,
            doctorName: p.doctor_name
        })) as Prescription[];
    } catch (err: any) {
        console.warn("Error fetching full history:", err.message);
        return [];
    }
  }
  
  async getValidPrescriptionsForCustomer(customerId: string): Promise<CategoryQuota[]> {
      if (!isSupabaseConfigured() || !this.supabase) {
        const now = new Date();
        return [{ categoryId: 'CAT001', categoryName: 'Flower', remainingQuota: 28.5, dosageUnit: 'GRAM', expiresAt: new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000).toISOString() }];
      }
      const { data, error } = await this.supabase.rpc('get_valid_prescriptions_for_customer', { p_customer_id: customerId });
      if (error) throw new Error(error.message);
      return (data || []).map((q: any) => ({
            categoryId: q.categoryId, 
            categoryName: q.categoryName, 
            remainingQuota: q.remainingQuota, 
            expiresAt: q.expiresAt,
            dosageUnit: q.dosageUnit
        })).filter((q: CategoryQuota) => {
            const isExpired = q.expiresAt ? new Date(q.expiresAt) < new Date() : false;
            return q.remainingQuota > 0 && !isExpired;
        }) as CategoryQuota[];
  }

  async getPendingPrescriptions(): Promise<Prescription[]> {
    if (!isSupabaseConfigured() || !this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_pending_prescriptions_for_store');
    if (error) throw new Error(error.message);
    return (data || []).map((p: any) => ({
      id: p.id,
      storeId: p.store_id,
      customerId: p.customer_id,
      createdById: p.created_by_id,
      doctorId: p.doctor_id,
      symptoms: p.symptoms,
      status: p.status,
      approvedItems: p.approved_items,
      notes: p.notes,
      createdAt: p.createdAt,
      reviewedAt: p.reviewed_at,
      evidenceUrl: p.evidence_url,
      customerName: p.customer_name,
      cashierName: p.cashier_name,
      customerBirthDate: p.customer_birth_date,
      customerCitizenId: p.customer_citizen_id,
      engaged_at: p.engaged_at,
      engaged_by: p.engaged_by,
      engaged_by_doctor_name: p.engaged_by_doctor_name,
      referred_by_store_name: p.referred_by_store_name, 
    }));
  }

  async getApprovedPrescriptions(): Promise<Prescription[]> {
    if (!isSupabaseConfigured() || !this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_approved_prescriptions_for_store');
    if (error) throw new Error(error.message);
    
    // The returned data structure from the new RPC is compatible with the Prescription model
    // so we can map it directly.
    return (data || []).map((p: any) => ({
      id: p.id,
      storeId: p.store_id,
      customerId: p.customer_id,
      createdById: p.created_by_id,
      doctorId: p.doctor_id,
      symptoms: p.symptoms,
      status: p.status,
      createdAt: p.createdAt,
      reviewedAt: p.reviewed_at,
      approvedItems: p.approved_items,
      customerName: p.customer_name,
      cashierName: p.cashier_name,
      customerBirthDate: p.customer_birth_date,
      customerCitizenId: p.customer_citizen_id,
      doctorName: p.doctor_name,
      referred_by_store_name: p.referred_by_store_name,
    })) as Prescription[];
  }

  async reviewPrescription(prescriptionId: string, status: 'APPROVED' | 'REJECTED', notes: string | null, approvedItems: PrescriptionDetail | null): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) throw new Error("Database not connected");
    const { error } = await this.supabase.rpc('review_prescription', {
      p_prescription_id: prescriptionId,
      p_status: status,
      p_notes: notes,
      p_approved_items: approvedItems,
    });
    if (error) {
      if (error.message.includes('INSUFFICIENT_FUNDS')) {
          throw new Error('ไม่สามารถอนุมัติได้: โควต้าแพ็กเกจหมด และเครดิตใน Wallet ไม่เพียงพอ กรุณาเติมเงินเข้าระบบ');
      }
      throw new Error(error.message);
    }
  }

  async cancelPrescription(prescriptionId: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) return;
    const { error } = await this.supabase.from(PRESCRIPTION_TABLE).update({ 
        status: 'REJECTED', 
        notes: 'Cancelled by patient at Kiosk (User Request)',
        reviewed_at: new Date().toISOString()
    }).eq('id', prescriptionId);
    if (error) throw new Error(error.message);
  }
  
  async engagePrescription(prescriptionId: string): Promise<any> {
    if (!isSupabaseConfigured() || !this.supabase) return;
    const { data, error } = await this.supabase.rpc('engage_prescription', { p_prescription_id: prescriptionId });
    if (error) throw error;
    return data;
  }
  
  async releasePrescription(prescriptionId: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) return;
    const { error } = await this.supabase.rpc('release_prescription', { p_prescription_id: prescriptionId });
    if (error) throw error;
  }

  async recordDispensing(prescriptionId: string, dispensedData: any): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) return;
    const { error } = await this.supabase.rpc('record_dispensing', {
        p_prescription_id: prescriptionId,
        p_dispensed_items: dispensedData
    });
    if (error) throw new Error(error.message);
  }

  async uploadEvidence(prescriptionId: string, base64Image: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) return;
    const { error } = await this.supabase.rpc('update_prescription_evidence', {
        p_prescription_id: prescriptionId,
        p_evidence_data: base64Image
    });
    if (error) throw new Error(error.message);
  }

  async getPrescriptionForPrinting(prescriptionId: string): Promise<any | null> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return null; 
    }

    try {
        const { data, error } = await this.supabase
          .rpc('get_prescription_details_secure', { p_prescription_id: prescriptionId });

        if (error) throw new Error(error.message);

        if (!data) return null;

        return {
          id: data.id,
          customerId: data.customer_id,
          storeId: data.store_id,
          status: data.status,
          createdAt: data.createdAt, 
          reviewedAt: data.reviewed_at, 
          approvedItems: data.approved_items,
          doctorName: data.doctor_name, 
          doctor_signature_snapshot: data.doctor_signature_snapshot,
          cashierName: data.cashier_name,
          customer: data.customer,
          providerDetails: data.provider_details
        };

    } catch (e: any) {
        console.error("Critical: Failed to fetch prescription data.", e.message);
        return null;
    }
  }
}
